import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {Element | null} */
  #observedBuyButtonsBlock = null;

  /** @type {Element | null} */
  #observedFooter = null;

  /** @type {Element | null} */
  #productInformationSection = null;

  /** @type {Element | null} */
  #stickyStopSection = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #setupAttempts = 0;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('scroll', this.#handleScrollCheck, { signal, passive: true });
    window.addEventListener('resize', this.#handleScrollCheck, { signal });
    this.addEventListener('change', this.#handleStickyControlChange, { signal });
    this.addEventListener('input', this.#handleStickyQuantityInput, { signal });
    this.addEventListener('click', this.#handleStickyControlClick, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    if (this.#buyButtonsIntersectionObserver) return;

    const productForm = this.#getProductForm();
    if (!productForm) {
      if (this.#setupAttempts < 20) {
        this.#setupAttempts += 1;
        window.setTimeout(() => this.#setupIntersectionObserver(), 100);
      }
      return;
    }

    const buyButtonsBlock = productForm.closest('.buy-buttons-block') ?? productForm;
    this.#observedBuyButtonsBlock = buyButtonsBlock;
    this.#productInformationSection = this.closest('.product-information') ?? this.closest('.shopify-section');
    this.#stickyStopSection =
      document.querySelector('[id*="allbetter_photo_reviews"]') ??
      document.querySelector('.ab-photo-reviews') ??
      document.querySelector('product-recommendations');

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) {
      if (this.#setupAttempts < 20) {
        this.#setupAttempts += 1;
        window.setTimeout(() => this.#setupIntersectionObserver(), 100);
      }
      return;
    }
    this.#observedFooter = footer;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          if (this.#shouldShowStickyBar()) {
            this.#showStickyBar();
          }
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (this.#shouldShowStickyBar()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = this.#getDirectCheckoutButton(productForm);
    this.#updateStickyVisibilityFromScroll();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.refs.addToCartButton.disabled) return;

    this.#syncMainQuantity(this.#currentQuantity);
    const variantId = this.#getCurrentVariantId();
    if (!variantId) return;

    const { addToCartButton } = this.refs;
    const label = addToCartButton.querySelector('.add-to-cart-text__content span span');
    const originalText = label?.textContent || '';

    addToCartButton.disabled = true;
    addToCartButton.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'REDIRECTING...';

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: this.#currentQuantity,
            },
          ],
        }),
      });
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Unable to add item.');
      }

      this.#redirectToCheckout();
    } catch (error) {
      console.error(error);
      addToCartButton.disabled = false;
      addToCartButton.removeAttribute('aria-busy');
      if (label) label.textContent = originalText;
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = this.#getDirectCheckoutButton(productForm);
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#syncStickyQuantityAfterMorph();
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateStickyQuantityControls();
    this.#updateButtonText();
  };

  /**
   * Handles changes from the sticky bar controls.
   * @param {Event} event
   */
  #handleStickyControlChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.hasAttribute('data-sticky-option-select')) {
      this.#syncMainVariantOption(target);
      return;
    }

    if (target instanceof HTMLInputElement && target.hasAttribute('data-sticky-quantity-input')) {
      this.#setStickyQuantity(Number(target.value), true);
    }
  };

  /**
   * Handles live typing in the sticky quantity input.
   * @param {Event} event
   */
  #handleStickyQuantityInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-sticky-quantity-input')) return;

    this.#setStickyQuantity(Number(target.value), true);
  };

  /**
   * Handles sticky quantity stepper buttons.
   * @param {MouseEvent} event
   */
  #handleStickyControlClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const minusButton = target.closest('[data-sticky-quantity-minus]');
    const plusButton = target.closest('[data-sticky-quantity-plus]');
    if (!minusButton && !plusButton) return;

    event.preventDefault();

    const rules = this.#getStickyQuantityRules();
    const direction = plusButton ? 1 : -1;
    this.#setStickyQuantity(this.#currentQuantity + rules.step * direction, true);
  };

  /**
   * Keeps sticky visibility reliable when IntersectionObserver misses initial layout.
   */
  #handleScrollCheck = () => {
    if (this.#scrollTicking) return;

    this.#scrollTicking = true;
    window.requestAnimationFrame(() => {
      this.#scrollTicking = false;
      this.#updateStickyVisibilityFromScroll();
    });
  };

  /**
   * Updates sticky visibility from measured positions.
   */
  #updateStickyVisibilityFromScroll() {
    if (!this.#observedBuyButtonsBlock || !this.#observedFooter) return;

    const buyButtonsRect = this.#observedBuyButtonsBlock.getBoundingClientRect();
    const buyButtonsPassed = buyButtonsRect.bottom < 0 || buyButtonsRect.top < 0;

    if (buyButtonsPassed && this.#shouldShowStickyBar()) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
    } else {
      this.#hiddenByBottom = !this.#shouldShowStickyBar();
      this.#hideStickyBar();
    }
  }

  /**
   * Keeps the floating purchase bar out of long-form content sections.
   */
  #shouldShowStickyBar() {
    this.#stickyStopSection =
      this.#stickyStopSection ??
      document.querySelector('[id*="allbetter_photo_reviews"]') ??
      document.querySelector('.ab-photo-reviews') ??
      document.querySelector('product-recommendations');

    document.documentElement.classList.remove('ab-sticky-compact');

    const footerRect = this.#observedFooter?.getBoundingClientRect();
    const footerIsNear = footerRect ? footerRect.top < window.innerHeight + 200 : false;
    return !footerIsNear;
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the currently selected variant id from the main product form.
   * @returns {string}
   */
  #getCurrentVariantId() {
    const productForm = this.#getProductForm();
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) return variantInput.value;

    return this.dataset.currentVariantId || '';
  }

  /**
   * Sends the customer straight to checkout after the sticky bar adds the product.
   */
  #redirectToCheckout() {
    try {
      window.top.location.assign('/checkout');
    } catch (error) {
      window.location.assign('/checkout');
    }
  }

  /**
   * Gets the direct-checkout product form button, falling back to any add-to-cart button.
   * @param {Element} productForm
   * @returns {HTMLButtonElement | null}
   */
  #getDirectCheckoutButton(productForm) {
    const directButton = productForm.querySelector('[ref="addToCartButton"][data-direct-checkout="true"]');
    if (directButton instanceof HTMLButtonElement) return directButton;

    const fallbackButton = productForm.querySelector('[ref="addToCartButton"]');
    return fallbackButton instanceof HTMLButtonElement ? fallbackButton : null;
  }

  /**
   * Gets the main product variant picker.
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    return sectionElement.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * Syncs a sticky option select to the main product variant picker.
   * @param {HTMLSelectElement} select
   */
  #syncMainVariantOption(select) {
    const optionIndex = Number(select.dataset.optionPosition || '1') - 1;
    const value = select.value;
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker || Number.isNaN(optionIndex)) return;

    const mainSelect = Array.from(variantPicker.querySelectorAll('select.variant-option__select'))[optionIndex];
    if (mainSelect instanceof HTMLSelectElement) {
      if (mainSelect.value !== value) {
        mainSelect.value = value;
        mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const radio = Array.from(variantPicker.querySelectorAll(`input[data-fieldset-index="${optionIndex}"]`)).find(
      (input) => input instanceof HTMLInputElement && input.value === value
    );

    if (radio instanceof HTMLInputElement && !radio.checked) {
      radio.click();
    }
  }

  /**
   * Reads the sticky quantity input constraints.
   * @returns {{min: number, max: number|null, step: number}}
   */
  #getStickyQuantityRules() {
    const input = this.querySelector('[data-sticky-quantity-input]');
    if (!(input instanceof HTMLInputElement)) return { min: 1, max: null, step: 1 };

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : null;
    const step = Number(input.step) || 1;

    return { min, max, step };
  }

  /**
   * Sets sticky quantity and optionally syncs it to the main product form.
   * @param {number} quantity
   * @param {boolean} syncMain
   */
  #setStickyQuantity(quantity, syncMain) {
    const rules = this.#getStickyQuantityRules();
    const parsedQuantity = Number.isFinite(quantity) ? quantity : rules.min;
    const boundedQuantity = Math.min(rules.max ?? Infinity, Math.max(rules.min, parsedQuantity));
    const steppedQuantity = rules.min + Math.round((boundedQuantity - rules.min) / rules.step) * rules.step;

    this.#currentQuantity = Math.min(rules.max ?? Infinity, Math.max(rules.min, steppedQuantity));
    this.#updateStickyQuantityControls();
    this.#updateButtonText();

    if (syncMain) {
      this.#syncMainQuantity(this.#currentQuantity);
    }
  }

  /**
   * Syncs the sticky quantity to the main product quantity selector.
   * @param {number} quantity
   */
  #syncMainQuantity(quantity) {
    const productForm = this.#getProductForm();
    const quantitySelector = productForm?.querySelector('quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input[ref="quantityInput"]');

    if (quantitySelector && 'setValue' in quantitySelector) {
      quantitySelector.setValue(quantity.toString());
      quantitySelector.updateButtonStates?.();
      quantitySelector.onQuantityChange?.();
      return;
    }

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantity.toString();
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Keeps sticky quantity controls aligned after server morphs.
   */
  #syncStickyQuantityAfterMorph() {
    this.#setStickyQuantity(this.#currentQuantity, false);
  }

  /**
   * Updates sticky quantity input and button disabled states.
   */
  #updateStickyQuantityControls() {
    const input = this.querySelector('[data-sticky-quantity-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const rules = this.#getStickyQuantityRules();
    input.value = this.#currentQuantity.toString();

    const minusButton = this.querySelector('[data-sticky-quantity-minus]');
    const plusButton = this.querySelector('[data-sticky-quantity-plus]');

    if (minusButton instanceof HTMLButtonElement) {
      minusButton.disabled = this.#currentQuantity <= rules.min;
    }

    if (plusButton instanceof HTMLButtonElement) {
      plusButton.disabled = rules.max !== null && this.#currentQuantity >= rules.max;
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateStickyQuantityControls();
    this.#updateButtonText();
  }

  /**
   * Keeps the sticky button label fixed.
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    quantityNumber.textContent = '';
    quantityDisplay.style.display = 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
